import { FilterList } from '@mui/icons-material';
import { Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { type ReactNode, useEffect, useState } from 'react';

import { trpc } from '../../../../trpc';
import { content } from '../../../../utils';
import { NumberInput } from '../../NumberInput';
import { FilterChips } from './FilterChips';
import { type FilterParameters, getAllFilterCountText } from './ResourceCalendarFilters.utils';
import { ResourceCalendarFiltersDrawer } from './ResourceCalendarFiltersDrawer';

type ResourceCalendarFiltersProps = {
  readonly onFilterChange: (filter: FilterParameters) => void;
  readonly filterParameters: FilterParameters;
  readonly startYear: number;
  readonly hiddenFilters?: (keyof FilterParameters)[];
  readonly lockedFilters?: Partial<Record<'regionSapIds' | 'standortSapIds' | 'locationSapIds', boolean>>;
  readonly padding?: number;
};

const CalendarFilterSelectedLabel = (more: number): ReactNode => (
  <Typography variant="paragraph">{`${more} ausgewählt`}</Typography>
);

export const ResourceCalendarFilters = ({
  onFilterChange,
  filterParameters,
  startYear,
  hiddenFilters,
  lockedFilters,
  padding,
}: ResourceCalendarFiltersProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const availableRegions = trpc.planung.resources.getAvailableRegionen.useQuery(undefined, { initialData: [] });
  const availableStandorte = trpc.planung.resources.getAvailableStandorteByRegion.useQuery(
    { regionSapIds: filterParameters.regionSapIds },
    { initialData: [] },
  );
  const availableLocations = trpc.planung.resources.getAvailableLocations.useQuery(
    { startYear, standortSapIds: filterParameters.standortSapIds, regionSapIds: filterParameters.regionSapIds },
    { initialData: [] },
  );

  // if selected standort/location IDs are no longer available after parent-filter changes, remove/clear them.
  useEffect(() => {
    if (availableStandorte.isFetching || availableLocations.isFetching) return;

    const availableStandortSapIds = new Set(availableStandorte.data.map((s) => s.standortSapId));
    const availableLocationSapIds = new Set(availableLocations.data.map((l) => l.locationSapId));

    const clearedStandortSapIds = lockedFilters?.standortSapIds
      ? filterParameters.standortSapIds
      : filterParameters.standortSapIds.filter((s) => availableStandortSapIds.has(s));
    const clearedLocationSapIds = lockedFilters?.locationSapIds
      ? filterParameters.locationSapIds
      : filterParameters.locationSapIds.filter((l) => availableLocationSapIds.has(l));

    if (
      clearedStandortSapIds.length !== filterParameters.standortSapIds.length ||
      clearedLocationSapIds.length !== filterParameters.locationSapIds.length
    ) {
      onFilterChange({
        ...filterParameters,
        standortSapIds: clearedStandortSapIds,
        locationSapIds: clearedLocationSapIds,
      });
    }
  }, [
    availableStandorte.data,
    availableStandorte.isFetching,
    availableLocations.data,
    availableLocations.isFetching,
    filterParameters,
    lockedFilters?.locationSapIds,
    lockedFilters?.standortSapIds,
    onFilterChange,
  ]);

  const updateFilterValue = <K extends keyof FilterParameters>(key: K, value: FilterParameters[K]): void => {
    const filterChanged = filterParameters[key] !== value;
    if (filterChanged) {
      onFilterChange({
        ...filterParameters,
        [key]: value,
      });
    }
  };

  const deleteAllFilters = (): void => {
    onFilterChange({
      ...filterParameters,
      regionSapIds: lockedFilters?.regionSapIds ? filterParameters.regionSapIds : [],
      standortSapIds: lockedFilters?.standortSapIds ? filterParameters.standortSapIds : [],
      locationSapIds: lockedFilters?.locationSapIds ? filterParameters.locationSapIds : [],
      locationArten: [],
      raumgroesse: null,
      locationHatGruppenraum: null,
      freieTage: null,
      sitzordnung: null,
      teilnehmerErwartet: null,
    });
  };

  const selectedStandorte = availableStandorte.data.filter((standort) =>
    filterParameters.standortSapIds.includes(standort.standortSapId),
  );
  const selectedLocations = availableLocations.data.filter((location) =>
    filterParameters.locationSapIds.includes(location.locationSapId),
  );

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ p: padding ?? 3 }}>
      <Stack direction="row" display="flex" spacing={2}>
        <FormControl sx={{ width: '12rem', pt: 1 }}>
          <Autocomplete
            id="standort-input"
            disabled={lockedFilters?.standortSapIds === true}
            size="small"
            multiple
            limitTags={0}
            getLimitTagsText={CalendarFilterSelectedLabel}
            value={selectedStandorte}
            onChange={(_, newValues) => {
              updateFilterValue(
                'standortSapIds',
                newValues.map((ort) => ort.standortSapId),
              );
            }}
            options={availableStandorte.data}
            getOptionLabel={(opt) => opt.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Auswählen"
                label="Standort"
                sx={{
                  '& input': {
                    display: () => (selectedStandorte.length > 0 ? 'none' : undefined),
                  },
                }}
              />
            )}
          />
        </FormControl>

        <FormControl sx={{ width: '12rem', pt: 1 }}>
          <Autocomplete
            id="location-input"
            disabled={lockedFilters?.locationSapIds === true}
            size="small"
            multiple
            value={selectedLocations}
            onChange={(_, newValues) => {
              updateFilterValue(
                'locationSapIds',
                newValues.map((location) => location.locationSapId),
              );
            }}
            options={availableLocations.data}
            getOptionLabel={(opt) => opt.kuerzel}
            limitTags={0}
            getLimitTagsText={CalendarFilterSelectedLabel}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                placeholder="Location"
                sx={{
                  '& input': {
                    display: () => (selectedLocations.length > 0 ? 'none' : undefined),
                  },
                }}
              />
            )}
          />
        </FormControl>

        <Box sx={{ pt: 1 }}>
          <NumberInput
            id="raumgroesse-input"
            label="Raumgröße"
            tooltip={`Mindestraumgröße in ${content.units.qm}`}
            min={1}
            value={filterParameters.raumgroesse}
            onChange={(number): void => updateFilterValue('raumgroesse', number)}
          />
        </Box>

        <Button onClick={() => setDrawerOpen(true)} variant="text" size="small">
          <FilterList sx={{ mr: 1 }} /> Alle Filter {getAllFilterCountText(filterParameters)}
        </Button>
        <ResourceCalendarFiltersDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          updateFilterValue={updateFilterValue}
          deleteAllFilters={deleteAllFilters}
          filterParameters={filterParameters}
          availableRegions={availableRegions.data}
          availableStandorte={availableStandorte.data}
          availableLocations={availableLocations.data}
          hiddenFilters={hiddenFilters}
          lockedFilters={lockedFilters}
        />
      </Stack>
      <FilterChips
        filterParameters={filterParameters}
        onFilterChange={onFilterChange}
        deleteAllFilters={deleteAllFilters}
        availableRegions={availableRegions.data}
        availableStandorte={availableStandorte.data}
        availableLocations={availableLocations.data}
        lockedFilters={lockedFilters}
      />
    </Box>
  );
};
